import React, { useState, useEffect } from 'react';
import {
  Stack,
  TextField,
  FormControlLabel,
  Checkbox,
  Typography,
  Divider,
} from '@mui/material';
import { ApprovalOutlined, PersonOutline } from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../../../../app/hooks';
import { updateStep } from '../../../state';
import { isApprovalConfig, createDefaultApprovalConfig, ApprovalStepConfiguration } from '../../../../../services/WorkflowService';
import { useUserSearch } from '../../../hooks/useUserSearch';
import { GraphUser } from '../../../../../utils/MsGraphApiCall';
import { UserSearchAutocomplete } from '../common';
import { ApprovalTimeoutPicker } from './ApprovalTimeoutPicker';

interface ApprovalStepConfigProps {
  index: number;
}

export const ApprovalStepConfig: React.FC<ApprovalStepConfigProps> = ({ index }) => {
  const dispatch = useAppDispatch();
  const step = useAppSelector((state) => state.workflow.currentWorkflow?.steps[index]);

  const { results: users, isLoading: userSearchLoading, query: searchQuery, setQuery: setSearchQuery } = useUserSearch();
  const [selectedUser, setSelectedUser] = useState<GraphUser | null>(null);

  const { results: backupUsers, isLoading: backupSearchLoading, query: backupSearchQuery, setQuery: setBackupSearchQuery } = useUserSearch();
  const [selectedBackupUser, setSelectedBackupUser] = useState<GraphUser | null>(null);

  const rawConfig = step?.configuration;
  // Merge with defaults so older saved payloads still populate the UI instead of
  // falling through to a blank "new" config and wiping the displayed approver.
  const config: ApprovalStepConfiguration = React.useMemo(() => {
    const defaults = createDefaultApprovalConfig();
    if (!rawConfig || !isApprovalConfig(rawConfig)) return defaults;
    return { ...defaults, ...rawConfig } as ApprovalStepConfiguration;
  }, [rawConfig]);

  useEffect(() => {
    if (config.approverEmail && !selectedUser) {
      setSelectedUser({
        id: config.approverEmail,
        displayName: config.approverEmail,
        mail: config.approverEmail.includes('@') ? config.approverEmail : '',
        userPrincipalName: config.approverEmail,
        accountEnabled: true,
      });
    }
  }, [config.approverEmail, selectedUser]);

  useEffect(() => {
    if (config.backupApproverEmail && !selectedBackupUser) {
      setSelectedBackupUser({
        id: config.backupApproverEmail,
        displayName: config.backupApproverEmail,
        mail: config.backupApproverEmail.includes('@') ? config.backupApproverEmail : '',
        userPrincipalName: config.backupApproverEmail,
        accountEnabled: true,
      });
    }
  }, [config.backupApproverEmail, selectedBackupUser]);

  const updateConfig = (configUpdates: Partial<ApprovalStepConfiguration>) => {
    if (!step) return;
    dispatch(
      updateStep({
        index,
        updates: { configuration: { ...step.configuration, ...configUpdates } },
      })
    );
  };

  const handleUserSelect = (_event: React.SyntheticEvent, user: GraphUser | null) => {
    setSelectedUser(user);
    updateConfig({ approverEmail: user ? user.userPrincipalName || user.mail || '' : '' });
  };

  const handleBackupUserSelect = (_event: React.SyntheticEvent, user: GraphUser | null) => {
    setSelectedBackupUser(user);
    updateConfig({ backupApproverEmail: user ? user.userPrincipalName || user.mail || '' : '' });
  };

  if (!step) return null;

  return (
    <Stack
      spacing={2}
      sx={{ mt: 1, p: 2, backgroundColor: '#f8fafc', borderRadius: 1, border: '1px solid #e2e8f0' }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <ApprovalOutlined sx={{ color: '#64748b', fontSize: 18 }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1e293b' }}>
          Approval Configuration
        </Typography>
      </Stack>

      <UserSearchAutocomplete
        label="Approver"
        placeholder="Search for an approver..."
        value={selectedUser}
        users={users}
        isLoading={userSearchLoading}
        searchQuery={searchQuery}
        onInputChange={setSearchQuery}
        onSelect={handleUserSelect}
        required
      />

      <Divider sx={{ my: 1 }} />

      <Stack direction="row" spacing={1} alignItems="center">
        <PersonOutline sx={{ color: '#64748b', fontSize: 18 }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1e293b' }}>
          Backup Approver (Optional)
        </Typography>
      </Stack>
      <Typography variant="caption" sx={{ color: '#64748b' }}>
        A backup approver receives notifications and can approve/reject independently of the primary approver.
      </Typography>

      <UserSearchAutocomplete
        label="Backup Approver"
        placeholder="Search for a backup approver..."
        value={selectedBackupUser}
        users={backupUsers}
        isLoading={backupSearchLoading}
        searchQuery={backupSearchQuery}
        onInputChange={setBackupSearchQuery}
        onSelect={handleBackupUserSelect}
      />

      <Divider sx={{ my: 1 }} />

      <FormControlLabel
        control={
          <Checkbox
            checked={config.requireComment}
            onChange={(e) => updateConfig({ requireComment: e.target.checked })}
            size="small"
          />
        }
        label={<Typography variant="body2" sx={{ color: '#1e293b' }}>Require Comment</Typography>}
      />

      <ApprovalTimeoutPicker
        duration={config.timeoutDuration}
        unit={config.timeoutUnit}
        onDurationChange={(d) => updateConfig({ timeoutDuration: d })}
        onUnitChange={(u) => updateConfig({ timeoutUnit: u })}
      />

      <TextField
        size="small"
        type="number"
        label="Reminder Interval (hours)"
        value={config.reminderIntervalHours ?? ''}
        onChange={(e) => {
          const raw = e.target.value;
          const parsed = raw === '' ? null : Math.max(1, Number(raw));
          updateConfig({ reminderIntervalHours: parsed });
        }}
        helperText="Re-email the approver every N hours until decided. Leave blank for no reminders."
        inputProps={{ min: 1 }}
      />

      <TextField
        size="small"
        label="Approval Message"
        value={config.approvalMessage}
        onChange={(e) => updateConfig({ approvalMessage: e.target.value })}
        placeholder="Message shown to the approver..."
        multiline
        minRows={2}
        maxRows={4}
        InputProps={{
          // Theme's global fixed input height clips multiline text against the top border; undo it here.
          sx: {
            height: 'auto',
            alignItems: 'flex-start',
            '& .MuiInputBase-input': {
              paddingTop: '8px',
            }
          }
        }}
      />
    </Stack>
  );
};

export default ApprovalStepConfig;
